"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EventBus = void 0;
exports.getEventBus = getEventBus;
exports.destroyEventBus = destroyEventBus;
exports.getRegisteredNamespaces = getRegisteredNamespaces;
exports.hasEventBus = hasEventBus;
class EventBus {
    constructor(options = {}) {
        this.listeners = {};
        this.namespaces = {};
        this.maxListeners = options.maxListeners || 10;
    }
    /**
     * 订阅事件
     * @param event 事件名称
     * @param handler 事件处理器
     */
    on(event, handler) {
        this._addListener(event, handler, false);
        return this;
    }
    /**
     * 一次性订阅事件
     * @param event 事件名称
     * @param handler 事件处理器
     */
    once(event, handler) {
        this._addListener(event, handler, true);
        return this;
    }
    /**
     * 取消订阅事件
     * @param event 事件名称
     * @param handler 可选，指定要移除的事件处理器
     */
    off(event, handler) {
        if (!this.listeners[event]) {
            return this;
        }
        if (!handler) {
            // 移除该事件的所有监听器
            delete this.listeners[event];
            return this;
        }
        // 移除指定的监听器
        this.listeners[event] = this.listeners[event].filter((listener) => listener.handler !== handler);
        if (this.listeners[event].length === 0) {
            delete this.listeners[event];
        }
        return this;
    }
    /**
     * 发布事件
     * @param event 事件名称
     * @param payload 事件负载数据
     */
    emit(event, payload) {
        if (!this.listeners[event]) {
            return this;
        }
        // 复制一份监听器数组，避免在执行过程中修改数组导致的问题
        const listeners = [...this.listeners[event]];
        for (const listener of listeners) {
            try {
                listener.handler(payload);
                if (listener.once) {
                    this.off(event, listener.handler);
                }
            }
            catch (err) {
                console.error(`Error in event handler for "${String(event)}":`, err);
            }
        }
        return this;
    }
    /**
     * 获取指定事件的监听器数量
     * @param event 事件名称
     */
    listenerCount(event) {
        var _a;
        return ((_a = this.listeners[event]) === null || _a === void 0 ? void 0 : _a.length) || 0;
    }
    /**
     * 移除所有事件监听器
     */
    removeAllListeners() {
        this.listeners = {};
        return this;
    }
    /**
     * 获取或创建命名空间
     * @param namespace 命名空间名称
     */
    namespace(namespace) {
        if (!this.namespaces[namespace]) {
            this.namespaces[namespace] = new EventBus({
                maxListeners: this.maxListeners,
            });
        }
        return this.namespaces[namespace];
    }
    /**
     * 设置最大监听器数量
     * @param n 最大监听器数量
     */
    setMaxListeners(n) {
        this.maxListeners = n;
        return this;
    }
    _addListener(event, handler, once) {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        // 检查是否已存在相同的处理器
        const exists = this.listeners[event].some((listener) => listener.handler === handler);
        if (exists) {
            console.warn(`Handler already exists for event "${String(event)}"`);
            return;
        }
        // 检查监听器数量限制
        if (this.listeners[event].length >= this.maxListeners) {
            console.warn(`Max listeners (${this.maxListeners}) reached for event "${String(event)}"`);
            return;
        }
        this.listeners[event].push({ handler, once });
    }
}
exports.EventBus = EventBus;
// 简单的全局 EventBus 注册表
class GlobalEventBusRegistry {
    constructor() {
        this.registry = new Map();
    }
    get(namespace) {
        return this.registry.get(namespace);
    }
    set(namespace, eventBus) {
        this.registry.set(namespace, eventBus);
    }
    has(namespace) {
        return this.registry.has(namespace);
    }
    delete(namespace) {
        return this.registry.delete(namespace);
    }
    keys() {
        return Array.from(this.registry.keys());
    }
}
// 获取全局注册表实例
const getGlobalRegistry = () => {
    const globalKey = '__GLOBAL_EVENTBUS_REGISTRY__';
    // 尝试从 globalThis 获取
    if (typeof globalThis !== 'undefined') {
        if (!globalThis[globalKey]) {
            globalThis[globalKey] = new GlobalEventBusRegistry();
        }
        return globalThis[globalKey];
    }
    // 降级方案：创建模块级别的实例
    return new GlobalEventBusRegistry();
};
const globalEventBusRegistry = getGlobalRegistry();
/**
 * 获取或创建指定命名空间的 EventBus 实例
 * @param namespace 命名空间，默认为 'default'
 * @param options EventBus 配置选项
 * @returns EventBus 实例
 */
function getEventBus(namespace = 'default', options) {
    if (!globalEventBusRegistry.has(namespace)) {
        globalEventBusRegistry.set(namespace, new EventBus(options));
    }
    return globalEventBusRegistry.get(namespace);
}
/**
 * 销毁指定命名空间的 EventBus 实例
 * @param namespace 命名空间
 */
function destroyEventBus(namespace) {
    const bus = globalEventBusRegistry.get(namespace);
    if (bus) {
        bus.removeAllListeners();
        globalEventBusRegistry.delete(namespace);
        return true;
    }
    return false;
}
/**
 * 获取所有已注册的 EventBus 命名空间
 */
function getRegisteredNamespaces() {
    return globalEventBusRegistry.keys();
}
/**
 * 检查指定命名空间是否已注册
 */
function hasEventBus(namespace) {
    return globalEventBusRegistry.has(namespace);
}
// 默认导出一个全局实例（使用 'default' 命名空间）
const eventBus = getEventBus('default');
exports.default = eventBus;
